import { useEffect, useState, useRef, useMemo } from "react";
import {
  SvgLogo,
  SvgPlay,
  SvgPause,
  Svg1X,
  Svg05X,
  Svg2X,
  Svg4X,
  SvgPlayback,
  SvgClockTime,
  SvgDownload,
  SvgFullscreen,
  SvgAlertPrevPage,
  SvgAlertNextPage,
} from "ui/svgs";
import { Popover } from "react-tiny-popover";
import { Dropdown, Slider, Spin } from "antd";
import moment from "moment";
import { softTitle, downloadFromSrc } from "lib/utils";

import PresetGuardPicSlide from "./presetGuardPicSlide";
import type { SliderSingleProps } from "antd";

import PlaybackModal from "ui/playback/playback-modal";
import { useDebouncedCallback } from "use-debounce";

export default function DefaultPage({
  selectedCamera,
  curPreset,
  startEnd,
  setStartEnd,
  progressData,
  progressDataLoading,
}) {
  const refPlayerContainer = useRef(null);

  const [pause, setPause] = useState(true);
  const [playSpeed, setPlaySpeed] = useState(4);

  const [currentPicId, setCurrentPicId] = useState("");
  const currentIdRef = useRef("");
  const [allLoaded, setAllLoaded] = useState(false);

  const [playbackModalVisible, setPlaybackModalVisible] = useState(false);
  const [showStartEndSlider, setShowStartEndSlider] = useState(false);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const keyPressRef = useRef(false);
  const keyTimerRef = useRef(null);

  useEffect(() => {
    if (progressData.length === 0 && !progressDataLoading) {
      setAllLoaded(true);
    }
  }, [progressData, progressDataLoading]);

  useEffect(() => {
    if (pause) return;
    const doLoop = () => {
      const curIndex = currentIdRef.current
        ? progressData.findIndex(
            (item) => item.eventId === currentIdRef.current,
          )
        : -1;
      if (curIndex === progressData.length - 1) {
        setPause(true);
        return;
      }
      handlePosNextPic();
    };
    doLoop();
    const timer = setInterval(doLoop, 1000 / playSpeed);
    return () => {
      clearTimeout(timer);
    };
  }, [pause, playSpeed]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullScreen(false);
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const posToPicByIndex = (i: number) => {
    if (i < 0 || i >= progressData?.length) {
      return;
    }
    setCurrentPicId(progressData[i].eventId);
    currentIdRef.current = progressData[i].eventId;
  };

  const curEventData = useMemo(() => {
    return progressData.find((item) => item.eventId === currentPicId);
  }, [currentPicId, progressData]);

  const handleAllLoaded = (imgSrcArr: { eventId: string; src: string }[]) => {
    setAllLoaded(true);
    // replace the snapshotUrl with the loaded image src
    progressData.forEach((item) => {
      if (imgSrcArr.find((img) => img.eventId === item.eventId)) {
        item.snapshotUrl =
          imgSrcArr.find((img) => img.eventId === item.eventId)?.src ||
          item.snapshotUrl;
      }
    });
  };

  function handlePauseResume() {
    setPause(!pause);
  }

  function speedChange(speed) {
    setPlaySpeed(speed);
  }

  function renderSpeedIcon() {
    switch (playSpeed) {
      case 0.5:
        return <Svg05X />;
      case 1:
        return <Svg1X />;
      case 2:
        return <Svg2X />;
      case 4:
        return <Svg4X />;
    }
  }

  const marks: SliderSingleProps["marks"] = {
    0: "0",
    3: "3",
    6: "6",
    9: "9",
    12: "12",
    15: "15",
    18: "18",
    21: "21",
    24: "24",
  };

  const onSliderChange = useDebouncedCallback((values) => {
    if (values?.length < 2) {
      return;
    }
    setStartEnd(values);
  }, 500);

  function convertHourToAmPm(hour: number): string {
    if (hour === 0 || hour === 24) {
      return "12 AM";
    } else if (hour === 12) {
      return "12 PM";
    } else if (hour < 12) {
      return `${hour} AM`;
    } else {
      return `${hour - 12} PM`;
    }
  }

  function handleDownloadPresetPic() {
    const formattedName = moment(Number(curEventData?.createdAt)).format(
      "MM-DD hh_mm A",
    );
    const fileName = `${selectedCamera?.name}-${formattedName}.jpg`;
    downloadFromSrc(fileName, curEventData.snapshotUrl);
  }

  function handleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullScreen(false);
    } else {
      setIsFullScreen(true);
      const elem = refPlayerContainer.current;
      elem?.focus();
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        // Firefox
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        // Chrome, Safari, and Opera
        elem.webkitRequestFullscreen();
      }
    }
  }

  const handlePosNextPic = () => {
    const curIndex = currentIdRef.current
      ? progressData.findIndex((item) => item.eventId === currentIdRef.current)
      : -1;
    posToPicByIndex(curIndex + 1);
  };

  const handlePosPrevPic = () => {
    const curIndex = currentIdRef.current
      ? progressData.findIndex((item) => item.eventId === currentIdRef.current)
      : -1;
    posToPicByIndex(curIndex - 1);
  };

  useEffect(() => {
    if (progressData.length === 0 || !pause || !isFullScreen) {
      return;
    }
    const handleKeyDown = (e) => {
      if (keyPressRef.current) {
        return;
      }
      if (e.key === "ArrowLeft") {
        keyPressRef.current = true;
        keyTimerRef.current = setInterval(() => {
          handlePosPrevPic();
        }, 300);
        handlePosPrevPic();
      } else if (e.key === "ArrowRight") {
        keyPressRef.current = true;
        keyTimerRef.current = setInterval(() => {
          handlePosNextPic();
        }, 300);
        handlePosNextPic();
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        keyPressRef.current = false;
        clearInterval(keyTimerRef.current);
      }
    };

    refPlayerContainer.current?.addEventListener("keydown", handleKeyDown);
    refPlayerContainer.current?.addEventListener("keyup", handleKeyUp);

    return () => {
      refPlayerContainer.current?.removeEventListener("keydown", handleKeyDown);
      refPlayerContainer.current?.removeEventListener("keyup", handleKeyUp);
      clearInterval(keyTimerRef.current);
    };
  }, [progressData, pause, isFullScreen]);

  return (
    <>
      {!curPreset ? (
        <div className="flex-grow flex items-center justify-center text-lg text-zinc-500">
          Please select a preset
        </div>
      ) : (
        <div className="p-6 flex-grow overflow-x-hidden">
          <div className="h-full flex flex-col gap-4 overflow-x-hidden">
            <div className="flex-grow w-full   overflow-hidden flex items-center justify-center relative object-cover">
              {progressDataLoading ? (
                <div className="w-full h-full flex justify-center items-center">
                  <Spin size="large" />
                </div>
              ) : progressData.length === 0 ? (
                <div className="flex items-center justify-center w-full h-full text-lg text-zinc-500">
                  No data available
                </div>
              ) : (
                <div
                  className="h-full aspect-video max-w-full max-h-full flex items-center justify-center relative group/play-div bg-[#EEEEEE] rounded-2xl"
                  ref={refPlayerContainer}
                  tabIndex={0}
                >
                  {curEventData?.snapshotUrl ? (
                    <>
                      <img
                        src={curEventData?.snapshotUrl}
                        alt="Camera Snapshot"
                        className="aspect-video rounded-lg w-full max-h-full relative  select-none"
                      />
                      <div className="absolute aspect-video rounded-lg w-full h-full invisible group-hover/play-div:visible">
                        <div className="flex items-center justify-around absolute bottom-4 right-4 w-[320px] h-[60px] rounded-2xl border border-black border-opacity-30 bg-[#222222] bg-opacity-25">
                          <button
                            disabled={
                              !pause ||
                              curEventData?.eventId ===
                                progressData?.at(0)?.eventId
                            }
                            className="w-[28px] h-[28px] flex flex-shrink-0 items-center justify-center hover:fill-zinc-200 disabled:hover:bg-transparent disabled:fill-zinc-400 fill-white"
                            onClick={handlePosPrevPic}
                          >
                            <SvgAlertPrevPage width="14" />
                          </button>

                          <button
                            disabled={!allLoaded || progressData?.length === 0}
                            className="w-[28px] h-[28px] flex flex-shrink-0 items-center justify-center hover:fill-zinc-200 disabled:hover:bg-transparent disabled:fill-zinc-400 fill-white"
                            onClick={handlePauseResume}
                          >
                            {!allLoaded ? (
                              <div
                                className="text-neutral-500 inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                                role="status"
                              ></div>
                            ) : pause ? (
                              <SvgPlay width="28" />
                            ) : (
                              <SvgPause width="28" />
                            )}
                          </button>

                          <button
                            disabled={
                              !pause ||
                              curEventData?.eventId ===
                                progressData?.at(progressData?.length - 1)
                                  ?.eventId
                            }
                            className="w-[28px] h-[28px] flex flex-shrink-0 items-center justify-center hover:fill-zinc-200 disabled:hover:bg-transparent disabled:fill-zinc-400 fill-white"
                            onClick={handlePosNextPic}
                          >
                            <SvgAlertNextPage width="14" />
                          </button>

                          <button
                            onClick={() => setPlaybackModalVisible(true)}
                            className="w-[28px] h-[28px] flex flex-shrink-0 items-center justify-center hover:fill-zinc-200 disabled:hover:bg-transparent fill-white"
                          >
                            <SvgPlayback width="25" />
                          </button>
                          <button
                            onClick={handleDownloadPresetPic}
                            className="w-[28px] h-[28px] flex flex-shrink-0 items-center justify-center hover:fill-zinc-200 disabled:hover:bg-transparent fill-white"
                          >
                            <SvgDownload width="25" />
                          </button>
                          <button
                            onClick={handleFullscreen}
                            className="w-[28px] h-[28px] flex flex-shrink-0 items-center justify-center hover:fill-zinc-200 disabled:hover:bg-transparent fill-white"
                          >
                            <SvgFullscreen width="25" />
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center">
                      <SvgLogo width="80" className="fill-neutral-400" />
                      <p className="text-neutral-400 font-semibold text-2xl">
                        {softTitle()}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className=" h-[86px] w-full bg-[#EEEEEE] rounded-2xl flex justify-center items-center gap-2 pl-2 pr-4 flex-shrink-0">
              <Popover
                isOpen={showStartEndSlider}
                positions={["top", "bottom", "left", "right"]}
                onClickOutside={() => setShowStartEndSlider(false)}
                content={
                  <div className="px-4 py-2 rounded-2xl w-64 bg-[#ffffff] border shadow progressTimeSlider">
                    <Slider
                      range
                      defaultValue={startEnd}
                      //value={startEnd}
                      marks={marks}
                      max={24}
                      styles={{
                        rail: { background: "#e1e1e1" },
                        track: { background: "#3e83f6" },
                        handle: {
                          background: "#3e83f6",
                          borderColor: "#3e83f6",
                        },
                      }}
                      tooltip={{
                        formatter: (value) => {
                          if (value == 24) {
                            return "23:59";
                          }
                          return `${value}:00`;
                        },
                      }}
                      onChange={onSliderChange}
                    />
                  </div>
                }
              >
                <button
                  className="w-[123px] h-[35px] border border-[#9E9E9E] font-semibold flex-shrink-0 flex gap-1 items-center text-sm justify-center rounded-xl hover:bg-zinc-200 disabled:hover:bg-transparent"
                  onClick={() => setShowStartEndSlider(true)}
                >
                  <SvgClockTime width="17" className="fill-zinc-500" />
                  {convertHourToAmPm(startEnd?.at(0))}
                  {" - "}
                  {convertHourToAmPm(startEnd?.at(1))}
                </button>
              </Popover>
              <button
                disabled={!allLoaded || progressData?.length === 0}
                className="w-[60px] h-[60px] flex-shrink-0 flex items-center justify-center rounded-xl hover:bg-zinc-200 disabled:hover:bg-transparent fill-neutral-800 disabled:fill-neutral-400 disabled:cursor-not-allowed"
                onClick={handlePauseResume}
              >
                {!allLoaded ? (
                  <div
                    className="text-neutral-500 inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                    role="status"
                  ></div>
                ) : pause ? (
                  <SvgPlay width="28" />
                ) : (
                  <SvgPause width="28" />
                )}
              </button>
              <Dropdown
                overlayClassName="virtualGuardProgress__speedDropdown"
                menu={{
                  items: [
                    {
                      label: "1x",
                      key: 2,
                      onClick: () => speedChange(1),
                    },
                    {
                      label: "2x",
                      key: 3,
                      onClick: () => speedChange(2),
                    },
                    {
                      label: "4x",
                      key: 4,
                      onClick: () => speedChange(4),
                    },
                  ],
                }}
                placement="top"
                arrow
              >
                <button className="w-[60px] h-[60px] flex-shrink-0  flex items-center justify-center rounded-xl hover:bg-zinc-200">
                  {renderSpeedIcon()}
                </button>
              </Dropdown>
              <div className="flex-grow overflow-x-hidden relative">
                <PresetGuardPicSlide
                  data={progressData}
                  pause={pause}
                  currentPicId={currentPicId}
                  setCurrentPicId={(cur) => {
                    setCurrentPicId(cur);
                    currentIdRef.current = cur;
                  }}
                  onAllLoaded={handleAllLoaded}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <PlaybackModal
        orgId={+selectedCamera?.orgId}
        visible={playbackModalVisible}
        onClose={() => setPlaybackModalVisible(false)}
        cameraId={selectedCamera?.id}
        // alertTime={+curEventData?.createdAt}
        startAt={+curEventData?.createdAt - 1000 * 60 * 10}
        endAt={+curEventData?.createdAt + 1000 * 60 * 5}
        initialSeekAt={+curEventData?.createdAt}
      />
    </>
  );
}
